// src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { preferencesApi, tokenManager } from '../services/api';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Check localStorage for saved theme, default to 'dark'
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('cubemate-theme');
    return (savedTheme as Theme) || 'dark';
  });

  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);

  // Load theme from backend on mount (if user is authenticated)
  useEffect(() => {
    const loadThemeFromBackend = async () => {
      const token = tokenManager.getToken();
      if (!token) {
        console.log('[THEME CONTEXT] No token found, skipping backend sync');
        setIsLoadingPreferences(false);
        return;
      }

      try {
        console.log('[THEME CONTEXT] Loading theme from backend...');
        const preferences = await preferencesApi.getPreferences();
        if (preferences.theme && preferences.theme !== theme) {
          console.log('[THEME CONTEXT] Syncing theme from backend:', preferences.theme);
          setThemeState(preferences.theme as Theme);
          localStorage.setItem('cubemate-theme', preferences.theme);
        }
      } catch (error) {
        console.error('[THEME CONTEXT] Failed to load theme from backend:', error);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadThemeFromBackend();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Apply theme class to document root immediately (no waiting for backend)
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    if (isLoadingPreferences) {
      // Apply class but don't sync to backend yet
      return;
    }

    // Save to localStorage
    localStorage.setItem('cubemate-theme', theme);

    // Sync to backend (if authenticated)
    const syncThemeToBackend = async () => {
      const token = tokenManager.getToken();
      if (!token) {
        console.log('[THEME CONTEXT] No token, skipping backend sync');
        return;
      }

      try {
        console.log('[THEME CONTEXT] Syncing theme to backend:', theme);
        await preferencesApi.updatePreferences({ theme });
        console.log('[THEME CONTEXT] ✅ Theme synced to backend');
      } catch (error) {
        console.error('[THEME CONTEXT] ❌ Failed to sync theme to backend:', error);
      }
    };

    syncThemeToBackend();
  }, [theme, isLoadingPreferences]);

  const toggleTheme = () => {
    console.log('[THEME CONTEXT] Toggling theme');
    setThemeState(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newTheme: Theme) => {
    console.log('[THEME CONTEXT] Setting theme to:', newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
